import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, CATEGORY_OPTIONS } from "@/lib/api";
import { AdminShell } from "@/pages/admin/AdminShell";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ImageUploader } from "@/components/ImageUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = {
  title: "", category: "", description: "", regular_price: "", sale_price: "",
  images: [], stock_status: "in_stock", stock_count: 0, featured: false,
};

export default function ProductForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      api.get(`/products/${id}`).then((r) => {
        const p = r.data;
        setForm({
          title: p.title, category: p.category, description: p.description || "",
          regular_price: p.regular_price, sale_price: p.sale_price ?? "",
          images: p.images || [], stock_status: p.stock_status,
          stock_count: p.stock_count ?? 0, featured: !!p.featured,
        });
      });
    }
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.title.trim()) return setError("Product title is required");
    if (!form.category) return setError("Please select a category");
    if (!form.regular_price || Number(form.regular_price) <= 0) return setError("Enter a valid regular price");
    if (form.sale_price && Number(form.sale_price) >= Number(form.regular_price))
      return setError("Sale price must be lower than the regular price");

    setSaving(true);
    const payload = {
      title: form.title.trim(),
      category: form.category,
      description: form.description,
      regular_price: Number(form.regular_price),
      sale_price: form.sale_price ? Number(form.sale_price) : null,
      images: form.images,
      stock_status: form.stock_status,
      stock_count: Number(form.stock_count) || 0,
      featured: form.featured,
    };
    try {
      if (editing) await api.put(`/products/${id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell active="products">
      <Link to="/admin" className="mb-6 inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900">
        <ArrowLeft size={15} /> Back to products
      </Link>
      <h1 className="font-display mb-6 text-2xl font-light">{editing ? "Edit Product" : "Add Product"}</h1>

      <form onSubmit={submit} className="grid gap-8 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="border border-zinc-200 bg-white p-6">
            <div className="space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Product Title</Label>
                <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 rounded-none border-zinc-300" data-testid="pf-title" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Description</Label>
                <div className="mt-1.5">
                  <RichTextEditor value={form.description} onChange={(v) => set("description", v)} placeholder="Describe the product, benefits, ingredients…" />
                </div>
              </div>
            </div>
          </div>

          <div className="border border-zinc-200 bg-white p-6">
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Product Images</Label>
            <div className="mt-3">
              <ImageUploader images={form.images} onChange={(imgs) => set("images", imgs)} />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="border border-zinc-200 bg-white p-6">
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="mt-1.5 rounded-none border-zinc-300" data-testid="pf-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((c) => (
                  <SelectItem key={c.slug} value={c.slug} data-testid={`pf-cat-${c.slug}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="mt-5 flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Featured</Label>
              <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="pf-featured" />
            </div>
          </div>

          <div className="border border-zinc-200 bg-white p-6">
            <p className="mb-4 text-xs uppercase tracking-wider text-zinc-500">Pricing</p>
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-zinc-500">Regular Price ($)</Label>
                <Input type="number" step="0.01" min="0" value={form.regular_price} onChange={(e) => set("regular_price", e.target.value)} className="mt-1.5 rounded-none border-zinc-300" data-testid="pf-regular-price" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Sale Price ($) — optional</Label>
                <Input type="number" step="0.01" min="0" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} className="mt-1.5 rounded-none border-zinc-300" data-testid="pf-sale-price" />
                <p className="mt-1 text-xs text-zinc-400">Adding a sale price shows an "On Sale" badge.</p>
              </div>
            </div>
          </div>

          <div className="border border-zinc-200 bg-white p-6">
            <p className="mb-4 text-xs uppercase tracking-wider text-zinc-500">Inventory</p>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-zinc-500">In Stock</Label>
              <Switch
                checked={form.stock_status === "in_stock"}
                onCheckedChange={(v) => set("stock_status", v ? "in_stock" : "out_of_stock")}
                data-testid="pf-stock-status"
              />
            </div>
            <div className="mt-4">
              <Label className="text-xs text-zinc-500">Stock Count</Label>
              <Input type="number" min="0" value={form.stock_count} onChange={(e) => set("stock_count", e.target.value)} className="mt-1.5 rounded-none border-zinc-300" data-testid="pf-stock-count" />
            </div>
          </div>

          {error && <p className="text-sm text-kksale" data-testid="pf-error">{error}</p>}

          <Button type="submit" disabled={saving} className="h-12 w-full rounded-none bg-zinc-900 text-xs uppercase tracking-widest hover:bg-zinc-800" data-testid="pf-submit">
            {saving ? "Saving…" : editing ? "Update Product" : "Create Product"}
          </Button>
        </div>
      </form>
    </AdminShell>
  );
}
